import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
// Variant gallery filtering
(function () {
  function filterGalleryByVariant(gallery, variantId) {
    const raw = gallery.dataset.variantMediaMap;
    if (!raw) return;
    let map;
    try { map = JSON.parse(raw); } catch (e) { return; }
    const allowed = map[String(variantId)];
    if (!allowed || allowed.length === 0) return;

    const isMobile = window.innerWidth < 750;

    if (isMobile) {
      const slideshowComponent = gallery.querySelector('slideshow-component');
      gallery.querySelectorAll('slideshow-slide').forEach((slide) => {
        const mediaDiv = slide.querySelector('[data-media-id]');
        if (!mediaDiv) return;
        const isAllowed = allowed.includes(String(mediaDiv.dataset.mediaId));
        if (isAllowed) {
          slide.removeAttribute('hidden');
        } else {
          slide.setAttribute('hidden', '');
        }
      });
          // Sync dots visibility with hidden slides
    const allSlides = Array.from(gallery.querySelectorAll('slideshow-slide[ref]'));
    const dotItems = Array.from(gallery.querySelectorAll('.slideshow-controls__dots li'));
    allSlides.forEach((slide, i) => {
      if (dotItems[i]) {
        dotItems[i].style.display = slide.hasAttribute('hidden') ? 'none' : '';
      }
    });
      // Force slideshow to re-sync to first visible slide
      if (slideshowComponent && typeof slideshowComponent.select === 'function') {
        requestAnimationFrame(() => slideshowComponent.select(0, undefined, { animate: false }));
      } else {
        const scroller = gallery.querySelector('slideshow-slides');
        if (scroller) scroller.scrollTo({ left: 0, behavior: 'instant' });
      }
    } else {
      gallery.querySelectorAll('li.product-media-container').forEach((slide) => {
        const mediaDiv = slide.querySelector('[data-media-id]');
        if (!mediaDiv) return;
        slide.style.display = allowed.includes(String(mediaDiv.dataset.mediaId)) ? '' : 'none';
        slide.style.visibility = '';
      });
    }

    gallery.classList.remove('filtering');

    // Filter thumbnails
    gallery.querySelectorAll('[data-media-id]').forEach((el) => {
      if (el.closest('slideshow-slide') || el.closest('li.product-media-container')) return;
      el.style.display = allowed.includes(String(el.dataset.mediaId)) ? '' : 'none';
    });
  }

  function getVariantIdFromUrl() {
    return new URLSearchParams(window.location.search).get('variant');
  }

  const observer = new MutationObserver(() => {
    const gallery = document.querySelector('media-gallery[data-variant-media-map]');
    if (!gallery) return;
    const variantId = getVariantIdFromUrl() || document.querySelector('[name="id"]')?.value;
    if (variantId) filterGalleryByVariant(gallery, variantId);
  });

  observer.observe(document.querySelector('.shopify-section') || document.body, {
    childList: true,
    subtree: true
  });

  document.addEventListener('DOMContentLoaded', () => {
    const gallery = document.querySelector('media-gallery[data-variant-media-map]');
    const variantId = getVariantIdFromUrl() || document.querySelector('[name="id"]')?.value;
    if (gallery && variantId) filterGalleryByVariant(gallery, variantId);
  });
})();

const _replaceWith = Element.prototype.replaceWith;
Element.prototype.replaceWith = function(...nodes) {
  const isGallery = this.tagName?.toLowerCase() === 'media-gallery';
  _replaceWith.apply(this, nodes);
  if (isGallery) {
    const newGallery = document.querySelector('media-gallery[data-variant-media-map]');
    if (!newGallery) return;
    const variantId = new URLSearchParams(location.search).get('variant')
      || document.querySelector('[name="id"]')?.value;
    if (!variantId) return;
    const raw = newGallery.dataset.variantMediaMap;
    if (!raw) return;
    let map;
    try { map = JSON.parse(raw); } catch(e) { return; }
    const allowed = map[String(variantId)];
    if (!allowed || !allowed.length) return;

    const isMobile = window.innerWidth < 750;

    if (isMobile) {
      const slideshowComponent = newGallery.querySelector('slideshow-component');
      newGallery.querySelectorAll('slideshow-slide').forEach((slide) => {
        const mediaDiv = slide.querySelector('[data-media-id]');
        if (!mediaDiv) return;
        const isAllowed = allowed.includes(String(mediaDiv.dataset.mediaId));
        if (isAllowed) {
          slide.removeAttribute('hidden');
        } else {
          slide.setAttribute('hidden', '');
        }
      });
          // Sync dots visibility with hidden slides
    const allSlides = Array.from(newGallery.querySelectorAll('slideshow-slide[ref]'));
    const dotItems = Array.from(newGallery.querySelectorAll('.slideshow-controls__dots li'));
    allSlides.forEach((slide, i) => {
      if (dotItems[i]) {
        dotItems[i].style.display = slide.hasAttribute('hidden') ? 'none' : '';
      }
    });

      if (slideshowComponent && typeof slideshowComponent.select === 'function') {
        requestAnimationFrame(() => slideshowComponent.select(0, undefined, { animate: false }));
      } else {
        const scroller = newGallery.querySelector('slideshow-slides');
        if (scroller) scroller.scrollTo({ left: 0, behavior: 'instant' });
      }
    } else {
      newGallery.querySelectorAll('li.product-media-container').forEach((slide) => {
        const mediaDiv = slide.querySelector('[data-media-id]');
        if (!mediaDiv) return;
        slide.style.display = allowed.includes(String(mediaDiv.dataset.mediaId)) ? '' : 'none';
      });
    }
  }
};

